import type { Line } from "./commands";

/**
 * The guided first run: landing page to first fill, one step at a time.
 *
 * A pure module, like `commands.ts` and `ticker.ts`. It takes what the shell
 * already knows and returns the step you are on plus the lines to print; it
 * fetches nothing, holds no timer, and can be read end to end to find out what
 * the tour says.
 *
 * **Every step is a predicate over state, not a counter.** That is the whole
 * design: a tour that remembers "you are on step 3" is wrong the moment
 * somebody does step 4 by hand, signs out, or arrives with an account already
 * open. Asking "is there a session / an account / a position" instead means the
 * tour is always correct about where you actually are, survives a reload for
 * free, and cannot be got out of step by typing the commands directly — which
 * is exactly what a curious person does. M4/E12.
 *
 * **And the numbers are derived, which they were not.** Each step used to carry
 * `"2 of 4"` in its own text while the tour printed only the step you were on —
 * so anybody arriving with a step already satisfied watched the count skip, and
 * a run that went 1, 3, 4 read as four steps with one missing. A number typed
 * into a sentence is a fact stored twice. M4/E25.
 */

export type OnboardingState = {
  signedIn: boolean;
  /**
   * Whether the terms of use have been accepted.
   *
   * Not a step — it is a blocking prompt, and a checklist item you cannot skip
   * is noise. It is here because the tour must be SILENT while it is
   * outstanding: the agreement is already asking for the user's attention, and
   * two sets of instructions at once is how a first run becomes confusing.
   */
  agreed: boolean;
  hasAccount: boolean;
  /**
   * Whether the user has asked for a price THEMSELVES — `watch`, `unwatch` or
   * `quote`.
   *
   * Not "is anything being watched", which was the first version and was always
   * true: the terminal watches five symbols from its first paint, so the step
   * was complete before the tour began and the run counted 1, 2, 4 out loud.
   * The lesson is that a predicate over state is only as good as the state it
   * reads, and "a price is on screen" was never the thing this step teaches.
   */
  pricedSomething: boolean;
  /**
   * Whether the trader has CHOSEN a feed, as opposed to being on the default.
   *
   * The server publishes the difference (`GET /api/v1/feeds` → `chosen`)
   * precisely so this predicate can exist. `selected` is always something —
   * everybody is being priced by *something* — so a step that read it would be
   * complete before the tour began, which is the mistake `pricedSomething`
   * already records one field up.
   */
  choseFeed: boolean;
  hasOrdered: boolean;
  hasPosition: boolean;
};

export type Step = {
  id: string;
  /** One line for the checklist. No number in it — see the note above. */
  title: string;
  /** True once this step no longer needs doing. */
  done: (state: OnboardingState) => boolean;
  /** What to say when you arrive at it: the exact thing to type. */
  say: readonly string[];
};

export const STEPS: readonly Step[] = [
  {
    id: "sign-in",
    title: "sign in",
    done: (s) => s.signedIn,
    say: [
      "type `login`. It asks for your email, sends a code, and asks for that.",
      "then you will be shown the terms of use to accept — it is short.",
    ],
  },
  {
    id: "account",
    title: "open your paper account",
    done: (s) => s.hasAccount,
    say: [
      "type `account create primary`. It opens one funded with 100,000.00,",
      "  priced by the simulator until you choose otherwise.",
    ],
  },
  {
    /*
     * Third, and deliberately AFTER the account since `M7/E33`.
     *
     * It was second, before the account, while a feed belonged to the trader:
     * every price they saw came from whatever they picked. A feed belongs to an
     * account now, so there is nothing to choose for until one is open — and
     * the choice is still made before the first price and the first trade,
     * which is the point the old order was protecting.
     *
     * It is also the honest place for it. The platform serves market data it
     * has no right to redistribute, so "bring your own entitlement" is not a
     * setting; it is the arrangement, and clause 4 of the terms says so.
     */
    id: "feed",
    title: "choose where your account's prices come from",
    done: (s) => s.choseFeed,
    say: [
      "type `feed`. It lists what may price your account, why each one is available,",
      "  and which brokers you can connect a key for — the simulator is always there.",
    ],
  },
  {
    id: "watch",
    title: "put a price on the screen",
    done: (s) => s.pricedSomething,
    say: ["type `watch NVDA` — or `quote AAPL` for one look without watching it."],
  },
  {
    id: "trade",
    title: "place your first trade",
    done: (s) => s.hasOrdered || s.hasPosition,
    say: [
      "type `buy 1 AAPL` — it is paper, so nothing real is bought.",
      "then `positions` shows what you hold and what it is worth.",
    ],
  },
];

/** The step you are on, or undefined when there is nothing left to do. */
export function currentStep(state: OnboardingState): Step | undefined {
  return STEPS.find((step) => !step.done(state));
}

export const FINISHED = [
  "that is the whole loop: sign in, fund, watch, trade.",
  "`help` lists everything else, and shows this list while a step is left.",
];

/**
 * The step to speak now, or nothing when the tour has nothing to add.
 *
 * **The tour is asked, never volunteering.** It used to advance on its own —
 * an effect watched the derived state and printed the next step the moment you
 * arrived at it, so a first run narrated itself. That was removed: unprompted
 * output in a terminal is the shell talking over you, and the one thing a
 * trader is doing while the tour speaks is reading something else.
 *
 * `checklist` answers "where am I" and this answers "what do I do next"; both
 * run only when `help` asks, while a step is left.
 */
export function say(state: OnboardingState): Line[] {
  if (state.signedIn && !state.agreed) return [];

  const step = currentStep(state);
  if (!step) return FINISHED.map((text) => ({ kind: "note" as const, text }));

  return [
    { kind: "out", text: `${position(step)} — ${step.title}.` },
    ...step.say.map((text) => ({ kind: "note" as const, text: `  ${text}` })),
  ];
}

/** `2 of 4`, worked out from where the step sits rather than from its text. */
function position(step: Step): string {
  return `${STEPS.indexOf(step) + 1} of ${STEPS.length}`;
}

/**
 * The whole tour as a checklist: what is done, where you are, what is left.
 *
 * Printed at the top of `help` while a step is left, so nothing is ever
 * only implied. A run that shows you one step at a time and silently swallows
 * the ones already behind you is a run you cannot count.
 */
export function checklist(state: OnboardingState): Line[] {
  const at = currentStep(state);
  return STEPS.map((step, i) => {
    const here = step.id === at?.id;
    const mark = step.done(state) ? "✓" : here ? "→" : " ";
    return {
      kind: here ? "out" : "note",
      text: `  ${mark} ${i + 1}. ${step.title}`,
      pre: true as const,
    };
  });
}
